import { Helmet } from 'react-helmet-async';
import { brand } from '../config/brand';

interface SeoProps {
  title?: string;
  description?: string;
  image?: string;
  /** Optional per-page SEO overrides (set in admin). */
  seoTitle?: string;
  seoDescription?: string;
  seoImageUrl?: string;
  /** Absolute canonical URL — emits <link rel="canonical"> when set. */
  canonicalUrl?: string;
  /** Open Graph object type. Use `article` for blog posts. */
  type?: 'website' | 'article';
  /** When true, emit a noindex robots directive (e.g. thin/utility pages). */
  noindex?: boolean;
  /** Optional JSON-LD payload — emits a <script type="application/ld+json"> tag. */
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
  /** Page language; drives og:locale. */
  lang?: 'en' | 'hi';
  /** hreflang alternates, e.g. [{ hrefLang: 'hi-IN', href: '...?lang=hi' }]. */
  alternates?: Array<{ hrefLang: string; href: string }>;
}

/**
 * Site-wide head manager. Per-page overrides (seoTitle / seoDescription /
 * seoImageUrl / canonicalUrl) take precedence over the defaults derived
 * from the page's primary content. Emits a complete Open Graph + Twitter
 * card set (with a branded default share image) so links render as polished,
 * trustworthy previews everywhere they are shared.
 */
export default function Seo({
  title,
  description,
  image,
  seoTitle,
  seoDescription,
  seoImageUrl,
  canonicalUrl,
  type = 'website',
  noindex = false,
  jsonLd,
  lang = 'en',
  alternates,
}: SeoProps) {
  const effectiveTitle = (seoTitle ?? '').trim() || title;
  const effectiveDescription = (seoDescription ?? '').trim() || description;
  const effectiveImage = (seoImageUrl ?? '').trim() || image || brand.ogImage;
  const fullTitle = effectiveTitle
    ? `${effectiveTitle} — Uclipse`
    : 'Uclipse — Industrial & Institutional Cleaning Concentrates';

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {effectiveDescription && <meta name="description" content={effectiveDescription} />}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large'} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {alternates?.map((a) => (
        <link key={a.hrefLang} rel="alternate" hrefLang={a.hrefLang} href={a.href} />
      ))}

      {/* Open Graph */}
      <meta property="og:site_name" content="Uclipse" />
      <meta property="og:locale" content={lang === 'hi' ? 'hi_IN' : 'en_IN'} />
      <meta property="og:title" content={fullTitle} />
      {effectiveDescription && <meta property="og:description" content={effectiveDescription} />}
      <meta property="og:image" content={effectiveImage} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:type" content={type} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {effectiveDescription && <meta name="twitter:description" content={effectiveDescription} />}
      <meta name="twitter:image" content={effectiveImage} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
